/**
 * SuccessStoryPage — TASK 10-1
 *
 * Public page at /success/<slug> that displays a user's branded achievement
 * share card.  No authentication required.
 *
 * OG meta tags are set dynamically so that Twitter/X, LinkedIn, WhatsApp,
 * and other crawlers render the share card image when the link is pasted.
 *
 * The public_slug is a random base62 string and never encodes the user_id.
 */
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Copy,
  Check,
  ExternalLink,
  Moon,
  Share2,
  Sun,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { supabase } from '../lib/supabase';
import { SEO } from '../shared/components/SEO';
import { siteUrl } from '../shared/seo/siteOrigin';
import { useTheme } from '../contexts/ThemeContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ShareCard {
  id:          string;
  headline:    string;
  role:        string;
  company:     string;
  image_url:   string;
  public_slug: string;
  created_at:  string;
}

interface SuccessStoryPageProps {
  slug:       string;
  onNavigate: (view: string) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function shareLinks(publicUrl: string, headline: string) {
  const urlEnc  = encodeURIComponent(publicUrl);
  const textEnc = encodeURIComponent(`${headline} 🎉 via @GoGrabJob #Hired #GoGrabJob`);
  return {
    twitter:  `https://twitter.com/intent/tweet?url=${urlEnc}&text=${textEnc}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${urlEnc}`,
    whatsapp: `https://wa.me/?text=${textEnc}%20${urlEnc}`,
  };
}

async function copyToClipboard(text: string) {
  if (navigator.clipboard) return navigator.clipboard.writeText(text);
  const el = document.createElement('textarea');
  el.value = text;
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SuccessStoryPage: React.FC<SuccessStoryPageProps> = ({ slug, onNavigate }) => {
  const { theme, toggleTheme } = useTheme();
  const [card,    setCard]    = useState<ShareCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied,  setCopied]  = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);

  useEffect(() => {
    if (!slug) { setNotFound(true); setLoading(false); return; }
    setLoading(true);

    supabase
      .from('share_cards')
      .select('id, headline, role, company, image_url, public_slug, created_at')
      .eq('public_slug', slug)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error || !data) {
          setNotFound(true);
        } else {
          setCard(data as ShareCard);
        }
        setLoading(false);
      });
  }, [slug]);

  const publicUrl = `${window.location.origin}/success/${slug}`;

  const handleCopy = async () => {
    await copyToClipboard(publicUrl);
    setCopied(true);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(false), 2200);
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-950 via-violet-900 to-purple-800">
        <div className="w-10 h-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
      </div>
    );
  }

  // ── 404 ───────────────────────────────────────────────────────────────────
  if (notFound || !card) {
    return (
      <>
        <SEO
          title="Share card not found · GoGrabJob"
          description="This share card doesn't exist or has been removed."
          canonical={siteUrl(`/success/${slug}`)}
          noIndex
        />
        <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-gradient-to-br from-purple-950 via-violet-900 to-purple-800 text-white p-6 text-center">
          <p className="text-5xl">🔍</p>
          <h1 className="text-2xl font-bold">Card not found</h1>
          <p className="text-white/70 max-w-sm">
            This success story link doesn't exist or may have been removed.
          </p>
          <button
            type="button"
            onClick={() => onNavigate('home')}
            className="px-6 py-3 rounded-xl bg-white/15 hover:bg-white/25 text-white font-semibold transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to GoGrabJob
          </button>
        </div>
      </>
    );
  }

  // ── Share page ────────────────────────────────────────────────────────────
  const links = shareLinks(publicUrl, card.headline);

  return (
    <>
      <SEO
        title={`${card.headline} · GoGrabJob`}
        description={`${card.role} at ${card.company} — shared via GoGrabJob, India's AI-powered job search platform.`}
        canonical={siteUrl(`/success/${slug}`)}
        ogImage={card.image_url}
        ogType="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type':    'Article',
          headline:   card.headline,
          author:     { '@type': 'Person', name: 'GoGrabJob User' },
          publisher:  { '@type': 'Organization', name: 'GoGrabJob', logo: { '@type': 'ImageObject', url: `${siteUrl()}/favicon.ico` } },
          image:      card.image_url,
          datePublished: card.created_at,
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-purple-950 via-violet-900 to-purple-800 flex flex-col">

        {/* ── Minimal nav ─────────────────────────────────────────────────── */}
        <header className="sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-black/20 backdrop-blur-md border-b border-white/10">
          <button
            type="button"
            onClick={() => onNavigate('home')}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm font-semibold touch-manipulation"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">GoGrabJob</span>
          </button>

          <span className="text-white font-bold tracking-tight">GoGrabJob</span>

          <button
            type="button"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors touch-manipulation"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </header>

        {/* ── Content ─────────────────────────────────────────────────────── */}
        <main className="flex-1 flex flex-col items-center justify-start px-4 py-10 gap-8">

          {/* Celebration heading */}
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center"
          >
            <p className="text-4xl mb-3">🎉</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white leading-snug">
              {card.headline}
            </h1>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-3 text-white/80 text-sm font-medium">
              <span className="flex items-center gap-1.5">
                <Briefcase className="w-4 h-4 opacity-70" />
                {card.role}
              </span>
              <span className="opacity-40">·</span>
              <span className="flex items-center gap-1.5">
                <Building2 className="w-4 h-4 opacity-70" />
                {card.company}
              </span>
            </div>
          </motion.div>

          {/* Share card image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl border border-white/10"
          >
            <img
              src={card.image_url}
              alt={card.headline}
              className="w-full object-cover"
              style={{ aspectRatio: '1200/630' }}
            />
          </motion.div>

          {/* Action card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="w-full max-w-md bg-white/10 backdrop-blur-md rounded-2xl border border-white/15 p-5 flex flex-col gap-3"
          >
            {/* Copy link */}
            <button
              type="button"
              onClick={handleCopy}
              className="w-full min-h-[48px] flex items-center justify-center gap-2 rounded-xl bg-white text-purple-900 font-semibold text-sm hover:bg-white/90 transition-colors touch-manipulation"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-emerald-600" />
                  Copied to clipboard!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy link
                </>
              )}
            </button>

            {/* Social share */}
            <div className="grid grid-cols-3 gap-2">
              <a
                href={links.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center justify-center gap-1 min-h-[56px] rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-medium transition-colors touch-manipulation"
              >
                <Share2 className="w-4 h-4" />
                X / Twitter
              </a>
              <a
                href={links.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center justify-center gap-1 min-h-[56px] rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-medium transition-colors touch-manipulation"
              >
                <ExternalLink className="w-4 h-4" />
                LinkedIn
              </a>
              <a
                href={links.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center justify-center gap-1 min-h-[56px] rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-medium transition-colors touch-manipulation"
              >
                <Share2 className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.35 }}
            className="text-center max-w-sm"
          >
            <p className="text-white/60 text-sm mb-4">
              Want to land your dream job too? GoGrabJob uses AI to optimise your resume
              and match you with the right opportunities.
            </p>
            <button
              type="button"
              onClick={() => onNavigate('signup')}
              className="px-8 py-3 rounded-xl bg-white text-purple-900 font-bold text-sm hover:bg-white/90 transition-colors shadow-lg touch-manipulation"
            >
              Get started free →
            </button>
          </motion.div>
        </main>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <footer className="py-6 text-center text-white/30 text-xs border-t border-white/10">
          © {new Date().getFullYear()} GoGrabJob · AI-powered job search for India
        </footer>
      </div>
    </>
  );
};

export default SuccessStoryPage;
